import { useQuery } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { keyBy } from "lodash";
import moment from "moment";
import { useState } from "react";
import {
  GoalArtifactNode,
  MeetingNode,
  UserStatus,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import GoalProgress from "@apps/goal-progress/goal-progress";
import { GoalProgressView } from "@apps/goal-progress/goal-progress-constants";
import InviteDialog from "@apps/invite-dialog/invite-dialog";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Error from "@components/error/error";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { assertEdgesNonNull, getUrl } from "@helpers/helpers";

import getHomepageDirectReportsMetadataQuery from "../graphql/get-homepage-direct-reports-metadata-query";
import {
  ReportOnboarding,
  deriveReportOnboarding,
  getOnboardedReportIds,
  isFullyOnboarded,
  isReportJoined,
} from "../helpers/onboarding";
import OnboardingProgress from "./onboarding-progress";
import { usePollForCalendarInsights } from "./use-poll-for-calendar-insights";

const directReportGoalMax = 3;

type DirectReportGoal = Pick<
  GoalArtifactNode,
  | "currentValue"
  | "id"
  | "isStale"
  | "progress"
  | "progressType"
  | "startValue"
  | "state"
  | "status"
  | "targetValue"
  | "title"
> & {
  readonly __typename: "GoalArtifactNode";
  readonly canUpdate: {
    readonly permission: boolean;
  };
};

type DirectReportMeeting = Pick<
  MeetingNode,
  "id" | "meetingGroupId" | "startDatetime"
>;

type DirectReportMetadataFromQuery = {
  readonly id: number;
  readonly firstName?: string | null;
  readonly name: string;
  readonly avatar?: string | null;
  readonly email?: string | null;
  readonly status?: UserStatus | null;
  readonly calendarInsightStatus?: string | null;
  readonly homepageStatus?: string | null;
  readonly goals?: {
    readonly totalCount: number;
    readonly edges: readonly ({
      readonly node?:
        | DirectReportGoal
        | { readonly id: number; readonly __typename?: string }
        | null;
    } | null)[];
  } | null;
  readonly meetings?: {
    readonly edges: readonly ({
      readonly node?: DirectReportMeeting | null;
    } | null)[];
  } | null;
};

type DirectReportMetadata = Omit<DirectReportMetadataFromQuery, "meetings"> & {
  meetings: DirectReportMeeting[];
};

type HomepageDirectReportsMetadataQuery = {
  readonly directReports?: {
    readonly edges: readonly ({
      readonly node?: DirectReportMetadataFromQuery | null;
    } | null)[];
  };
};

type HomepageDirectReportsMetadataQueryVariables = {
  organizationId: number;
  currentUserId: number;
  directReportIds: number[];
  goalMax: number;
  hasDirectReportIds: boolean;
  now: string;
};

const formatMeetingDate = (startDatetime?: string | null) =>
  startDatetime
    ? moment(startDatetime).format("ddd @ h:mma").replace(":00", "")
    : null;

// Strip markdown and collapse whitespace. The status is a short one-liner, so it is rendered in
// full with no truncation (TF-1500). Handles Slack mrkdwn links (<url|label>) as well as
// CommonMark links, and leaves literal hyphens intact so "1-on-1" survives.
const stripSummaryMarkdown = (summary?: string | null) =>
  (summary || "")
    .replace(/<[^|>]+\|([^>]+)>/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[`*_>#~]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const DirectReportSummary = ({
  homepageStatus,
  calendarInsightFallback,
}: {
  homepageStatus?: string | null;
  calendarInsightFallback?: string | null;
}) => {
  // Prefer the work-history homepage status; fall back to the cached calendar insight (TF-1429).
  const summary =
    stripSummaryMarkdown(homepageStatus) ||
    stripSummaryMarkdown(calendarInsightFallback);

  return (
    <p className="text-sm leading-5 text-gray-600">
      {summary || "No recent updates."}
    </p>
  );
};

const DirectReportCard = ({
  directReport,
  metadata,
  onboarding,
  loadingMetadata,
}: {
  directReport: BasicUser;
  metadata?: DirectReportMetadata;
  onboarding: ReportOnboarding;
  loadingMetadata: boolean;
}) => {
  const organization = currentOrganizationVar();
  const goals = metadata?.goals ? assertEdgesNonNull(metadata.goals) : [];
  const openGoals = goals.filter(
    (goal): goal is DirectReportGoal => goal.__typename === "GoalArtifactNode",
  );
  const remainingGoalCount =
    (metadata?.goals?.totalCount || openGoals.length) - openGoals.length;
  const nextMeeting = metadata?.meetings?.[0];
  const nextMeetingDate = formatMeetingDate(nextMeeting?.startDatetime);
  const fullyOnboarded = isFullyOnboarded(onboarding);

  return (
    <div className="relative rounded-md border border-gray-200 bg-white p-5 min-h-[108px] hover:bg-gray-50 hover:border-gray-300 hover:shadow-xs">
      {/*
        The whole card is a link to the report's profile. It is rendered as a stretched
        overlay so that interactive children (goal pills, the 1-on-1 chip, the onboarding
        breakdown) can sit above it with `relative z-10` and remain independently clickable.
      */}
      <AppLink
        to={getUrl({ userId: directReport.id })}
        aria-label={directReport.name}
        className="absolute inset-0 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
      />
      <div className="flex gap-3">
        <Avatar
          user={directReport}
          size="8"
          showTooltip={false}
          className="shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex items-center gap-2">
              <span className="truncate text-sm font-medium text-gray-900">
                {directReport.name}
              </span>
              {organization.featureFlags.goals && (
                <div className="relative z-10 flex shrink-0 items-center gap-1">
                  {loadingMetadata ? (
                    <div className="skeleton h-3 w-12 rounded-sm" />
                  ) : (
                    <>
                      {openGoals.map((goal) => (
                        <GoalProgress
                          key={goal.id}
                          artifact={goal}
                          withPopover
                          goalProgressView={GoalProgressView.pillOnly}
                        />
                      ))}
                      {remainingGoalCount > 0 && (
                        <AppLink
                          to={`/goals/user/${directReport.id}`}
                          className="text-xs font-medium text-gray-500 hover:underline"
                        >
                          +{remainingGoalCount}
                        </AppLink>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {fullyOnboarded ? (
              organization.featureFlags.meetings &&
              nextMeeting &&
              nextMeetingDate && (
                <AppLink
                  to={getUrl({
                    meetingGroupId: nextMeeting.meetingGroupId,
                    meetingId: nextMeeting.id,
                  })}
                  className="relative z-10 inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-gray-500 hover:underline"
                >
                  <TopicflowIcon type="calendar" className="h-4 w-4 shrink-0" />
                  Next: {nextMeetingDate}
                </AppLink>
              )
            ) : (
              <div className="relative z-10 shrink-0">
                <OnboardingProgress
                  report={directReport}
                  onboarding={onboarding}
                />
              </div>
            )}
          </div>
          <div className="mt-1">
            {loadingMetadata && !metadata ? (
              <div className="skeleton h-4 w-full rounded-sm" />
            ) : (
              <DirectReportSummary
                homepageStatus={metadata?.homepageStatus}
                calendarInsightFallback={
                  organization.featureFlags.workHistory
                    ? metadata?.calendarInsightStatus
                    : null
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DirectReportsComponent = () => {
  const currentUser = currentUserVar();
  const onboardedReportIds = getOnboardedReportIds(currentUser.onboardingData);
  const organization = currentOrganizationVar();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const directReports = currentUser.directReports
    ? assertEdgesNonNull(currentUser.directReports)
    : [];
  const directReportIds = directReports.map((directReport) => directReport.id);
  const { data, loading, refetch } = useQuery<
    HomepageDirectReportsMetadataQuery,
    HomepageDirectReportsMetadataQueryVariables
  >(getHomepageDirectReportsMetadataQuery, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
    skip: directReportIds.length === 0,
    variables: {
      organizationId: organization.id,
      directReportIds,
      hasDirectReportIds: directReportIds.length > 0,
      goalMax: directReportGoalMax,
      currentUserId: currentUser.id,
      now: moment().startOf("hour").format(),
    },
  });

  const usersWithMetadata = data?.directReports
    ? assertEdgesNonNull(data.directReports).map((user) => ({
        ...user,
        meetings: user.meetings ? assertEdgesNonNull(user.meetings) : [],
      }))
    : [];

  // Number of reports whose calendar insight has been generated so far.
  const populatedInsightCount = usersWithMetadata.filter(
    (user) => (user.calendarInsightStatus ?? "").trim().length > 0,
  ).length;

  usePollForCalendarInsights({
    populatedInsightCount,
    enabled:
      directReportIds.length > 0 && !!organization.featureFlags.workHistory,
    refetch,
  });

  if (directReports.length === 0) {
    return null;
  }

  const metadataByUserId = keyBy(usersWithMetadata, "id");
  const reportsToRender = usersWithMetadata.map((user) => ({
    id: user.id,
    firstName: user.firstName || undefined,
    name: user.name,
    avatar: user.avatar,
    email: user.email || undefined,
  }));

  // Per-report onboarding state, derived from real data: whether the manager has been
  // walked through this report (onboarded_report_ids), whether there is an upcoming
  // 1-on-1, and whether the report has joined the app.
  const fallbackOnboarding = deriveReportOnboarding({
    toldAboutReport: false,
    hasUpcomingOneonone: false,
    invited: false,
  });
  const onboardingByUserId: Record<number, ReportOnboarding> = {};
  usersWithMetadata.forEach((user) => {
    onboardingByUserId[user.id] = deriveReportOnboarding({
      toldAboutReport: onboardedReportIds.includes(user.id),
      hasUpcomingOneonone: user.meetings.length > 0,
      invited: isReportJoined(user.status),
    });
  });

  const renderHeader = () => (
    <>
      <div className="mb-4 flex items-center justify-between gap-4">
        <span className="tf-header-lg-dark">Your Direct Reports</span>
        <Button
          theme={buttonTheme.blueText}
          size={ButtonSize.medium}
          className="gap-1.5"
          onClick={() => setShowInviteDialog(true)}
        >
          <TopicflowIcon type="userPlus02" className="h-4 w-4 shrink-0" />
          Invite people
        </Button>
      </div>
      {showInviteDialog && (
        <InviteDialog
          organization={organization}
          onClose={() => setShowInviteDialog(false)}
        />
      )}
    </>
  );

  if (loading && reportsToRender.length === 0) {
    return (
      <Layout.MainSubSection title={renderHeader} className="mb-0">
        <div className="grid grid-cols-1 gap-4 @[640px]/layout-main:grid-cols-2">
          {directReports.map((directReport) => (
            <div
              key={directReport.id}
              className="rounded-md border border-gray-200 bg-white p-5 min-h-[108px]"
            >
              <div className="flex gap-3">
                <div className="skeleton h-8 w-8 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="skeleton h-4 w-32 rounded-sm" />
                    <div className="skeleton h-4 w-4 shrink-0 rounded-full" />
                  </div>
                  <div className="mt-3 skeleton h-4 w-full rounded-sm" />
                  <div className="mt-3 skeleton h-4 w-28 rounded-sm" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Layout.MainSubSection>
    );
  }

  if (reportsToRender.length === 0) {
    return null;
  }

  return (
    <Layout.MainSubSection title={renderHeader} className="mb-0">
      <div className="grid grid-cols-1 gap-4 @[640px]/layout-main:grid-cols-2">
        {reportsToRender.map((directReport) => (
          <DirectReportCard
            key={directReport.id}
            directReport={directReport}
            metadata={metadataByUserId[directReport.id]}
            onboarding={
              onboardingByUserId[directReport.id] ?? fallbackOnboarding
            }
            loadingMetadata={loading && !data}
          />
        ))}
      </div>
    </Layout.MainSubSection>
  );
};

const DirectReports = withErrorBoundary(DirectReportsComponent, {
  fallback: <Error description="Your direct reports could not be rendered." />,
});

export default DirectReports;
